import { useLocation, useNavigation } from '@remix-run/react';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Navbar, Nav, Offcanvas, Row, Col, Container, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import CurrencySwitcher from '~/components/CurrencySwitcher';
import Icon from '~/components/icons/Icon';
import LanguageSwitcher from '~/components/LanguageSwitcher';
import UserNavigation from '~/components/layout/header/UserNavigation';
import MegaColumn from '~/components/layout/MegaColumn';
import { useRoutePath } from '~/hooks/useRoutePath';
import type { Taxon } from '~/types/Taxon';
import { getChildren } from '~/utils/taxon';

interface MegaMenuProps {
  taxonTree: Taxon[];
  show: boolean;
  onHide: () => void;
}

const MegaMenu: React.FC<MegaMenuProps> = ({ taxonTree, show, onHide }) => {
  const visibleTaxons = taxonTree.filter((t) => t.display !== false);
  const parents = visibleTaxons.filter((t) => t.level === 1);
  const routePath = useRoutePath();
  const { t } = useTranslation();
  const navigation = useNavigation();
  const location = useLocation();

  const [openMobile, setOpenMobile] = useState<Record<string, boolean>>({});
  // Category thumbnails mount only once a dropdown has been opened. The panel is hidden with
  // visibility/opacity, so lazy loading alone would still fetch every image on first paint.
  const [revealedCodes, setRevealedCodes] = useState<Set<string>>(() => new Set());
  const [isClosed, setIsClosed] = useState(false);

  // Desktop hover-intent: which parent's dropdown is currently open (null = none).
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const SHOW_DELAY = 180;
  const HIDE_DELAY = 220;

  // Track if click happened - persists across re-renders during navigation
  const clickedRef = useRef(false);
  const prevLocationRef = useRef(location.pathname);

  const isNavigating = navigation.state !== 'idle';

  const revealParent = useCallback((code: string) => {
    setRevealedCodes((prev) => (prev.has(code) ? prev : new Set(prev).add(code)));
  }, []);

  const clearShowTimer = useCallback(() => {
    if (showTimer.current) {
      clearTimeout(showTimer.current);
      showTimer.current = null;
    }
  }, []);

  const clearHideTimer = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  // Clean up pending timers on unmount.
  useEffect(
    () => () => {
      clearShowTimer();
      clearHideTimer();
    },
    [clearShowTimer, clearHideTimer]
  );

  const handleItemEnter = useCallback(
    (code: string) => {
      clearHideTimer();
      revealParent(code);
      if (activeCode !== null) {
        // A submenu is already open — switch instantly.
        clearShowTimer();
        setActiveCode(code);
      } else {
        // Nothing open yet — wait, so a quick pass-over doesn't flash the panel.
        clearShowTimer();
        showTimer.current = setTimeout(() => setActiveCode(code), SHOW_DELAY);
      }
    },
    [activeCode, clearShowTimer, clearHideTimer, revealParent]
  );

  const handleItemLeave = useCallback(() => {
    // Cancel a pending first-open if the cursor left before it fired.
    clearShowTimer();
  }, [clearShowTimer]);

  // When location changes, keep menu closed if it was clicked
  useEffect(() => {
    if (location.pathname !== prevLocationRef.current) {
      prevLocationRef.current = location.pathname;
      if (clickedRef.current) {
        setIsClosed(true);
      }
    }
  }, [location.pathname]);

  const toggleMobile = (code: string) => {
    setOpenMobile((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const handleLinkClick = useCallback(() => {
    // Blur active element to prevent :focus-within from keeping menu open
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    clickedRef.current = true;
    setIsClosed(true);
    clearShowTimer();
    clearHideTimer();
    setActiveCode(null);
    onHide();
  }, [onHide, clearShowTimer, clearHideTimer]);

  const handleMenuEnter = useCallback(() => {
    // Re-entering the menu cancels a pending close.
    clearHideTimer();
  }, [clearHideTimer]);

  const handleMouseLeave = useCallback(() => {
    clickedRef.current = false;
    setIsClosed(false);
    // Leaving the menu/submenu: wait briefly, then hide (no animation).
    clearShowTimer();
    clearHideTimer();
    hideTimer.current = setTimeout(() => setActiveCode(null), HIDE_DELAY);
  }, [clearShowTimer, clearHideTimer]);

  // A dropdown only counts as open while the menu isn't being dismissed/navigated.
  const isOpen = (code: string) => activeCode === code && !isClosed && !isNavigating;

  return (
    <Container
      className={`megamenu${isClosed || isNavigating ? ' megamenu--closed' : ''}${
        activeCode !== null && !isClosed && !isNavigating ? ' megamenu--active' : ''
      }`}
      onMouseEnter={handleMenuEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Offcanvas show={show} onHide={onHide} placement="end" responsive="lg">
        <Offcanvas.Header closeButton className="mega-header">
          <Offcanvas.Title>{t('menu')}</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="mega-body">
          <Navbar className="w-100 p-0">
            <Nav as="ul" className="flex-column flex-lg-row w-100 mega-nav">
              {parents.map((parent) => {
                const hasBanner = false;
                const children = getChildren(visibleTaxons, parent.code);

                return (
                  <li
                    key={parent.slug}
                    className={`mega-item${isOpen(parent.code) ? ' is-open' : ''}`}
                    onMouseEnter={() => handleItemEnter(parent.code)}
                    onMouseLeave={handleItemLeave}
                    onFocus={() => revealParent(parent.code)}
                  >
                    <span className="d-flex align-items-center justify-content-between">
                      <Link
                        to={routePath('category', parent.slug)}
                        onClick={handleLinkClick}
                        className="mega-link d-flex align-items-center justify-content-between line-holder"
                      >
                        <span>{parent.name}</span>

                        {children.length > 0 && <Icon name="chevron-down" size={18} className="d-none d-lg-block" />}
                      </Link>
                      {children.length > 0 && (
                        <Button
                          variant="link"
                          className="p-0 d-block d-lg-none mega__toggle"
                          onClick={() => {
                            revealParent(parent.code);
                            toggleMobile(parent.slug);
                          }}
                          aria-expanded={openMobile[parent.slug]}
                          aria-controls={`mega-dropdown-${parent.slug}`}
                          aria-label={parent.slug}
                        >
                          <Icon
                            name="chevron-down"
                            size={24}
                            className={`mega__toggle-icon ${openMobile[parent.slug] ? 'open' : ''}`}
                          />
                        </Button>
                      )}
                    </span>

                    {children.length > 0 && (
                      <div
                        id={`mega-dropdown-${parent.slug}`}
                        className={`mega-dropdown ${openMobile[parent.slug] ? 'show' : ''}${
                          isOpen(parent.code) ? ' is-open' : ''
                        }`}
                      >
                        {hasBanner ? (
                          <Row>
                            <Col xs={12} xl={9}>
                              <Row xl={5}>
                                {children.map((child) => (
                                  <MegaColumn
                                    key={child.slug}
                                    child={child}
                                    taxonTree={visibleTaxons}
                                    openMobile={openMobile}
                                    toggleMobile={toggleMobile}
                                    onHide={handleLinkClick}
                                    routePath={routePath}
                                    renderImage={revealedCodes.has(parent.code)}
                                  />
                                ))}
                              </Row>
                            </Col>
                            <Col lg={9} xl={3} className="d-none d-lg-block">
                              <div className="h-100 d-flex align-items-start justify-content-end">
                                <Link to={routePath('homepage')} onClick={handleLinkClick}>
                                  <img
                                    src="/assets/images/hp_banner-1.jpg"
                                    alt="Promo banner"
                                    className="img-fluid"
                                    loading="lazy"
                                    fetchpriority="low"
                                    decoding="async"
                                    width={243}
                                    height={162}
                                  />
                                </Link>
                              </div>
                            </Col>
                          </Row>
                        ) : (
                          <Row xl={5}>
                            {children.map((child) => (
                              <MegaColumn
                                key={child.slug}
                                child={child}
                                taxonTree={visibleTaxons}
                                openMobile={openMobile}
                                toggleMobile={toggleMobile}
                                onHide={handleLinkClick}
                                routePath={routePath}
                                renderImage={revealedCodes.has(parent.code)}
                              />
                            ))}
                          </Row>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </Nav>
          </Navbar>
          <div className="d-lg-none mt-3 p-3 mega-footer bg-light">
            <UserNavigation />
            <div className="d-flex align-items-center gap-2 mt-3">
              <LanguageSwitcher />
              <CurrencySwitcher />
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
};

export default MegaMenu;
